import CampaignOutlined from '@mui/icons-material/CampaignOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import { alpha, useTheme } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import { MessagingChannelType } from '@treadinc/horizon-api-spec';

import { MessagingChannel } from '~hooks/useMessaging/models';
import {
  initialsOf,
  messagingAvatarColorFor,
  messagingChannelCounterpart,
} from '~pages/Messages/messagingAvatar';
import { Nullable } from '~types/Nullable';

export const MESSAGING_AVATAR_SIZE_IN_PX = 32;

export const MESSAGING_HEADER_AVATAR_SIZE_IN_PX = 36;

export const MESSAGING_BUBBLE_AVATAR_SIZE_IN_PX = 28;

// 12px initials on the 32px Job DMs avatar, kept in proportion at the other sizes.
const INITIALS_TO_AVATAR_RATIO = 0.375;

const GLYPH_TO_AVATAR_RATIO = 0.55;

type GlyphAvatar = { Glyph: typeof CampaignOutlined; isBrandTinted: boolean };

const GLYPH_AVATARS = {
  [MessagingChannelType.BROADCAST]: { Glyph: CampaignOutlined, isBrandTinted: true },
  [MessagingChannelType.DIRECT]: null,
  [MessagingChannelType.JOB]: null,
  [MessagingChannelType.ORDER]: { Glyph: ReceiptLongOutlined, isBrandTinted: false },
} satisfies Record<MessagingChannelType, Nullable<GlyphAvatar>>;

type GlyphChannelType = MessagingChannelType.BROADCAST | MessagingChannelType.ORDER;

type MessagingAvatarProps = {
  colorKey: string;
  name: string;
  size?: number;
};

export default function MessagingAvatar({
  colorKey,
  name,
  size = MESSAGING_AVATAR_SIZE_IN_PX,
}: MessagingAvatarProps) {
  const theme = useTheme();

  return (
    <Avatar
      data-test-id="messaging-avatar"
      sx={{
        bgcolor: messagingAvatarColorFor(colorKey, theme),
        color: theme.semantic.textOnBrand,
        fontSize: `${Math.round(size * INITIALS_TO_AVATAR_RATIO)}px`,
        fontWeight: 700,
        height: size,
        width: size,
      }}
    >
      {initialsOf(name) || undefined}
    </Avatar>
  );
}

type MessagingGlyphAvatarProps = {
  channelType: GlyphChannelType;
  size?: number;
};

export function MessagingGlyphAvatar({
  channelType,
  size = MESSAGING_AVATAR_SIZE_IN_PX,
}: MessagingGlyphAvatarProps) {
  const theme = useTheme();
  const { Glyph, isBrandTinted } = GLYPH_AVATARS[channelType];

  return (
    <Avatar
      data-test-id={`messaging-avatar-${channelType}`}
      sx={{
        bgcolor: isBrandTinted
          ? alpha(theme.brandV2.colors.treadOrangeDark, 0.16)
          : theme.semantic.surfaceRaised,
        border: isBrandTinted ? undefined : `1px solid ${theme.semantic.border}`,
        color: isBrandTinted
          ? theme.brandV2.colors.treadOrangeDark
          : theme.semantic.textSecondary,
        height: size,
        width: size,
      }}
    >
      <Glyph sx={{ fontSize: size * GLYPH_TO_AVATAR_RATIO }} />
    </Avatar>
  );
}

const isGlyphChannelType = (
  channelType: MessagingChannelType,
): channelType is GlyphChannelType => Boolean(GLYPH_AVATARS[channelType]);

type MessagingChannelAvatarProps = {
  channel: MessagingChannel;
  size?: number;
  viewerUserId: Nullable<string>;
};

export function MessagingChannelAvatar({
  channel,
  size,
  viewerUserId,
}: MessagingChannelAvatarProps) {
  const { channelType } = channel;

  if (isGlyphChannelType(channelType)) {
    return <MessagingGlyphAvatar channelType={channelType} size={size} />;
  }

  return (
    <MessagingAvatar
      {...messagingChannelCounterpart(channel, viewerUserId)}
      size={size}
    />
  );
}
